<template>
  <div class="bet-history-charts">
    <div class="bet-history-charts__bead" aria-label="Result roadmap">
      <div class="bet-history-charts__bead-grid">
        <div
          v-for="(cell, index) in beadCells"
          :key="`bead-${index}`"
          class="bet-history-charts__bead-cell"
          :class="{ 'has-result': cell }"
          @mouseenter="hoveredIndex = cell ? index : null"
          @mouseleave="hoveredIndex = null"
        >
          <span v-if="cell" class="bet-history-charts__bead-dot" :class="cell.isTai ? 'tai' : 'xiu'">
            {{ cell.value }}
          </span>
          <!-- As the cockfight road: the round number, the result, then the value. -->
          <div
            v-if="cell && hoveredIndex === index && hasRound(cell.round)"
            class="bet-history-charts__tooltip"
            :class="tooltipPlacement(index)"
          >
            <span class="bet-history-charts__tooltip-round">#{{ cell.round }}</span>
            <span v-if="cell.dice" class="bet-history-charts__tooltip-dice">
              <img v-for="(die, dieIndex) in cell.dice" :key="dieIndex" :src="diceImage(die)" alt="" />
            </span>
            <span class="bet-history-charts__tooltip-total">{{ cell.value }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="bet-history-charts__log" aria-label="Round history">
      <div
        v-for="(round, index) in roundLogs"
        :key="`log-${index}`"
        class="bet-history-charts__log-row"
        :title="round ? roundTitle(round.round) : null"
      >
        <template v-if="!round">
          <span v-for="cell in 5" :key="cell" />
        </template>
        <template v-else>
          <span
            v-for="(die, dieIndex) in round.dice"
            :key="dieIndex"
            class="bet-history-charts__log-die"
            :class="die <= 3 ? 'low' : 'high'"
          >
            {{ die }}
          </span>
          <span class="bet-history-charts__log-total" :class="round.isTai ? 'tai' : 'xiu'">
            {{ round.total }}
          </span>
          <span class="bet-history-charts__log-label" :class="round.isTai ? 'tai' : 'xiu'">
            {{ round.label }}
          </span>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
const BEAD_COLUMNS = 12;
const BEAD_ROWS = 6;
const LOG_ROWS = 6;

export default {
  props: {
    recentResults: {
      type: Array,
      default: () => [],
    },
    recentDice: {
      type: Array,
      default: () => [],
    },
    // The round number of each result, same order: what a hover on a result shows.
    recentRounds: {
      type: Array,
      default: () => [],
    },
    // The table's code, in front of the round number on hover (e.g. TXS #12).
    code: {
      type: String,
      default: '',
    },
  },
  data() {
    return { hoveredIndex: null };
  },
  computed: {
    beadCells() {
      const cells = Array(BEAD_COLUMNS * BEAD_ROWS).fill(null);
      const points = [];
      let column = 0;
      let row = 0;
      let runStartColumn = 0;
      let previousSide = null;
      const occupied = {};

      this.recentResults.forEach((value, resultIndex) => {
        const total = Number(value);
        if (!Number.isInteger(total) || total < 4 || total > 17) return;
        const side = total > 10 ? 'tai' : 'xiu';

        if (previousSide === null) {
          column = 0;
          row = 0;
        } else if (side === previousSide) {
          const nextRow = row + 1;
          if (nextRow < BEAD_ROWS && !occupied[`${column}:${nextRow}`]) {
            row = nextRow;
          } else {
            column += 1;
          }
        } else {
          runStartColumn += 1;
          column = runStartColumn;
          row = 0;
          while (occupied[`${column}:${row}`]) column += 1;
        }

        occupied[`${column}:${row}`] = true;
        points.push({
          column,
          row,
          value: total,
          isTai: total > 10,
          round: this.recentRounds[resultIndex],
          dice: this.recentDice[resultIndex] && this.recentDice[resultIndex].length === 3
            ? this.recentDice[resultIndex]
            : null,
        });
        previousSide = side;
      });

      const lastColumn = points.reduce((max, point) => Math.max(max, point.column), 0);
      const columnOffset = Math.max(0, lastColumn - BEAD_COLUMNS + 1);
      points.forEach((point) => {
        const visibleColumn = point.column - columnOffset;
        if (visibleColumn < 0 || visibleColumn >= BEAD_COLUMNS) return;
        cells[point.row * BEAD_COLUMNS + visibleColumn] = point;
      });
      return cells;
    },
    roundLogs() {
      // Always LOG_ROWS rows, newest first: with fewer results the rest stay
      // empty, rather than the few there are stretching to fill the box.
      const logs = this.recentResults.slice(-LOG_ROWS).reverse().map((value, reverseIndex) => {
        const total = Number(value);
        const isTai = total > 10;
        const index = this.recentResults.length - 1 - reverseIndex;
        const actualDice = this.recentDice[index];

        return {
          dice: actualDice && actualDice.length === 3 ? actualDice : this.diceFromTotal(total),
          round: this.recentRounds[index],
          total,
          isTai,
          label: isTai ? 'BIG' : 'SMALL',
        };
      });
      return logs.concat(Array(LOG_ROWS - logs.length).fill(null));
    },
  },
  methods: {
    // The road sits in a box that clips what overflows it: a tooltip opens
    // upward from the lower rows and leftward from the right-hand columns.
    tooltipPlacement(index) {
      return {
        'bet-history-charts__tooltip--up': Math.floor(index / BEAD_COLUMNS) >= BEAD_ROWS / 2,
        'bet-history-charts__tooltip--left': index % BEAD_COLUMNS >= BEAD_COLUMNS / 2,
      };
    },
    hasRound(round) {
      return round !== undefined && round !== null && round !== '';
    },
    diceImage(face) {
      return require(`@/assets/svg/dice-items/dice-${face}.svg`);
    },
    // Hover text for a result: its table and round, e.g. "TXS #12".
    roundTitle(round) {
      if (round === undefined || round === null || round === '') return null;
      return `${this.code ? `${this.code} ` : ''}#${round}`;
    },
    diceFromTotal(total) {
      let remaining = total;
      const first = Math.min(6, Math.max(1, Math.floor(remaining / 3)));
      remaining -= first;
      const second = Math.min(6, Math.max(1, Math.floor(remaining / 2)));
      return [first, second, Math.min(6, Math.max(1, remaining - second))];
    },
  },
};
</script>

<style lang="scss" scoped>
.bet-history-charts {
  display: flex;
  gap: 6px;
  margin-top: 8px;
  min-width: 0;
  flex-shrink: 0;
  height: 120px;
}

.bet-history-charts__bead {
  flex: 1 1 220px;
  min-width: 0;
  border-radius: 6px;
  border: 1px solid rgba(62, 83, 142, 0.65);
  background: rgba(21, 30, 55, 0.85);
  box-sizing: border-box;
}

.bet-history-charts__bead-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  grid-template-rows: repeat(6, minmax(0, 1fr));
  gap: 0;
  width: 100%;
  height: 100%;
}

.bet-history-charts__bead-cell {
  position: relative;
  min-width: 0;
  min-height: 0;
  border-right: 1px solid rgba(62, 83, 142, 0.65);
  border-bottom: 1px solid rgba(62, 83, 142, 0.65);
  box-sizing: border-box;

  &:nth-child(12n) {
    border-right: 0;
  }
}

.bet-history-charts__bead-cell.has-result:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

// The cockfight road's tooltip (charts/basic-chart.vue), dice for the bird.
.bet-history-charts__tooltip {
  position: absolute;
  top: 100%;
  left: 10px;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: #f0f0f0;
  color: #333;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  pointer-events: none;

  &--up {
    top: auto;
    bottom: 100%;
  }

  &--left {
    left: auto;
    right: 10px;
  }
}

.bet-history-charts__tooltip-round {
  font-weight: 700;
}

.bet-history-charts__tooltip-dice {
  display: inline-flex;
  gap: 2px;

  img {
    width: 16px;
    height: 16px;
    display: block;
  }
}

.bet-history-charts__bead-dot {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 10px;
  font-weight: 700;
  color: #fff;

  &.tai {
    background: #EB3323;
  }

  &.xiu {
    background: #0073E8;
  }
}

.bet-history-charts__log {
  flex: 0 0 146px;
  width: 146px;
  border-radius: 6px;
  border: 1px solid rgba(62, 83, 142, 0.65);
  background: rgba(21, 30, 55, 0.85);
  padding: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow: hidden;
}

.bet-history-charts__log-row {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr) 1.2fr 1.4fr;
  gap: 0;
  align-items: center;

  > span {
    height: 100%;
    border-right: 1px solid rgba(62, 83, 142, 0.65);
    border-bottom: 1px solid rgba(62, 83, 142, 0.65);
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  > span:last-child {
    border-right: 0;
  }

  &:last-child > span {
    border-bottom: 0;
  }
}

.bet-history-charts__log-die {
  font-size: 9px;
  font-weight: 700;
  color: #d5def0;
  text-align: center;
  line-height: 1;

  &.low {
    color: #0073E8;
  }

  &.high {
    color: #EB3323;
  }
}

.bet-history-charts__log-total {
  font-size: 10px;
  font-weight: 700;
  text-align: center;
  padding: 2px 0;
  color: #fff;

  &.tai {
    background: #EB3323;
  }

  &.xiu {
    background: #0273E8;
  }
}

.bet-history-charts__log-label {
  font-size: 9px;
  font-weight: 700;
  text-align: center;
  text-transform: uppercase;

  &.tai {
    color: #EB3323;
  }

  &.xiu {
    color: #0073E8;
  }
}
</style>
